/*
 * Product page behaviour (sections product-page-*).
 *
 * Two custom elements, no dependencies:
 *
 *   <pdp-gallery>    the media column. Carries the id MediaGallery-<section>
 *                    and a setActiveMedia(mediaId, prepend) method, because
 *                    that is the contract VariantSelects.updateMedia() in
 *                    global.js calls when the chosen variant has its own
 *                    image. Everything else about it is ours.
 *
 *   <pdp-accordion>  the detail rows. One row open at a time, matching the
 *                    design canvas, with the height animated from the
 *                    measured content rather than a fixed guess.
 *
 * The related products strip is not handled here: it reuses the shop overview
 * tile and therefore shop-overview.js.
 */

(() => {
  if (!window.customElements) return;

  class PdpGallery extends HTMLElement {
    connectedCallback() {
      this.thumbs = Array.from(this.querySelectorAll('[data-pdp-thumb]'));
      this.slides = Array.from(this.querySelectorAll('[data-pdp-slide]'));
      this.counter = this.querySelector('[data-pdp-counter]');
      // The template lives in the attribute so Liquid can render the first
      // state into the element and nothing flashes before this runs
      this.counterTemplate = this.counter ? this.counter.dataset.pdpCounter : '';

      this.thumbs.forEach((thumb) => {
        thumb.addEventListener('click', () => this.setActiveMedia(thumb.dataset.pdpThumb));
      });

      this.syncCounter();
    }

    /*
     * Called by us on a thumbnail click and by global.js on a variant change.
     * `prepend` is part of Dawn's signature and means "this media should lead
     * the gallery"; the rail here is in a fixed order, so it is accepted and
     * ignored.
     */
    setActiveMedia(mediaId) {
      const slide = this.slides.find((item) => item.dataset.mediaId === mediaId);
      if (!slide) return;

      this.pauseAll();

      this.slides.forEach((item) => item.classList.toggle('is-active', item === slide));
      this.thumbs.forEach((thumb) => {
        const current = thumb.dataset.pdpThumb === mediaId;
        thumb.setAttribute('aria-current', current ? 'true' : 'false');
        if (current) this.scrollThumbIntoView(thumb);
      });

      this.syncCounter();
    }

    /* Stops a video that is still running when the viewer moves on */
    pauseAll() {
      this.slides.forEach((slide) => {
        slide.querySelectorAll('video').forEach((video) => video.pause());
        slide.querySelectorAll('iframe').forEach((frame) => {
          // Re-assigning the src is the only way to stop a cross origin embed
          frame.src = frame.src; // eslint-disable-line no-self-assign
        });
      });
    }

    scrollThumbIntoView(thumb) {
      const rail = thumb.closest('[data-pdp-rail]');
      if (!rail) return;

      const railBox = rail.getBoundingClientRect();
      const thumbBox = thumb.getBoundingClientRect();
      const outside =
        thumbBox.top < railBox.top ||
        thumbBox.bottom > railBox.bottom ||
        thumbBox.left < railBox.left ||
        thumbBox.right > railBox.right;

      if (outside) thumb.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    }

    syncCounter() {
      if (!this.counter || !this.counterTemplate) return;

      const index = this.slides.findIndex((slide) => slide.classList.contains('is-active'));
      this.counter.textContent = this.counterTemplate
        .replace('@@current@@', index < 0 ? 1 : index + 1)
        .replace('@@total@@', this.slides.length);
    }
  }

  class PdpAccordion extends HTMLElement {
    connectedCallback() {
      this.rows = Array.from(this.querySelectorAll('[data-pdp-row]'));

      this.rows.forEach((row) => {
        const button = row.querySelector('[data-pdp-toggle]');
        if (!button) return;
        button.addEventListener('click', () => this.toggle(row));
      });

      this.rows.filter((row) => row.classList.contains('is-open')).forEach((row) => this.open(row));

      // A row opened on load is measured before its webfont lands
      window.addEventListener('resize', () => this.remeasure());
      if (document.fonts && document.fonts.ready) {
        document.fonts.ready.then(() => this.remeasure());
      }
    }

    toggle(row) {
      if (row.classList.contains('is-open')) {
        this.close(row);
        return;
      }

      this.rows.forEach((other) => other !== row && this.close(other));
      this.open(row);
    }

    open(row) {
      const panel = row.querySelector('[data-pdp-panel]');
      const button = row.querySelector('[data-pdp-toggle]');
      if (!panel) return;

      row.classList.add('is-open');
      if (button) button.setAttribute('aria-expanded', 'true');
      panel.style.maxHeight = `${panel.scrollHeight}px`;
    }

    close(row) {
      const panel = row.querySelector('[data-pdp-panel]');
      const button = row.querySelector('[data-pdp-toggle]');
      if (!panel) return;

      row.classList.remove('is-open');
      if (button) button.setAttribute('aria-expanded', 'false');
      panel.style.maxHeight = '0px';
    }

    remeasure() {
      this.rows
        .filter((row) => row.classList.contains('is-open'))
        .forEach((row) => {
          const panel = row.querySelector('[data-pdp-panel]');
          if (panel) panel.style.maxHeight = `${panel.scrollHeight}px`;
        });
    }
  }

  if (!customElements.get('pdp-gallery')) customElements.define('pdp-gallery', PdpGallery);
  if (!customElements.get('pdp-accordion')) customElements.define('pdp-accordion', PdpAccordion);
})();
